import { CardContainer, LoadingSpinner } from "@msys/ui";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { BreadcrumbItem, Page } from "../../../../commons/layout/Page.js";
import { PageContainer } from "../../../../commons/layout/PageContainer.js";
import {
  ActivityFeed,
  type ActivityFeedEntry,
} from "../pds-sync/ActivityFeed.js";
import {
  INTEGRATIONS_BASE_PATH,
  integrationDashboardPath,
  integrationSettingsPath,
} from "../../paths.js";

interface Props {
  integrationId: string;
  integrationName: string;
  title: string;
  children: React.ReactNode;
  onDisconnect?: () => void;
  removeLoading?: boolean;
  loading?: boolean;
  /** Recent integration events — rendered as a "Letzte Aktivität" card */
  events?: ActivityFeedEntry[];
  /** Refetch behind the events card's refresh button */
  onRefreshEvents?: () => Promise<unknown>;
  /** Show a link to the integration's dashboard (integrations with health) */
  hasDashboard?: boolean;
}

export const SettingsPageLayout = ({
  integrationId,
  integrationName,
  title,
  children,
  onDisconnect,
  removeLoading,
  loading,
  events,
  onRefreshEvents,
  hasDashboard,
}: Props) => {
  const { t } = useTranslate(["Integrations", "OrganisationPageTopbar"]);
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
    {
      title: integrationName,
      to: integrationSettingsPath(integrationId),
    },
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <Page title={title} breadcrumbs={breadcrumbs} breadcrumbsSize="medium">
      <PageContainer>
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 640 }}>
          {hasDashboard && (
            <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  navigate(integrationDashboardPath(integrationId))
                }
              >
                {t("Dashboard")}
              </Button>
            </Stack>
          )}

          {children}

          {events && events.length > 0 && (
            <ActivityFeed events={events} onRefresh={onRefreshEvents} />
          )}

          {onDisconnect && (
            <CardContainer title={t("Disconnect")}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 1,
                  pt: 0.5,
                  pb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "No longer using this integration? You can disconnect it here."
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setConfirmOpen(true)}
                  disabled={removeLoading}
                >
                  {t("Disconnect")}
                </Button>
              </Stack>
            </CardContainer>
          )}
        </Stack>
      </PageContainer>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {t("Disconnect {name}?", {
            name: integrationName,
            ns: "Integrations",
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "The connection will be removed. Data already transferred remains in MeisterSystems. To reconnect you may need your access credentials."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmOpen(false);
              onDisconnect?.();
            }}
          >
            {t("Disconnect")}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};
